import React, {useEffect, useRef, useState} from "react";
import {useOutsiderDetector} from "../../../Hooks/useOutsiderDetector";
import {GetObjectIndexByKeyValue} from "../../../Constants/Helpers";
import useComboBox from "./useComboBox";
import useFilterSelectOptions from "../../../Hooks/useFilterSelectOptions";
import {ComboBoxType} from "./ComboBoxTypes";
import ComboBoxListActions from "./Components/ComboBoxListActions";
import ComboBoxNoResults from "./Components/ComboBoxNoResults";
import ComboBoxFilter from "./Components/ComboBoxFilter";
import {SelectItemType} from "../../../Types/FormTypes";
import ComboBoxItemToggle from "./Components/ComboBoxItemToggle";

const ComboBox = (props: ComboBoxType) => {
    const [ comboData, setComboData ] = useState<any>(props.data);
    const [ active, setActive ] = useState<boolean>(props.listAlwaysVisible ?? false);
    const [ displayValue, setDisplayValue ] = useState<string>('');

    const multiSelectRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLInputElement>(null);

    const FilterStartCallback = () => {
        if(!active) {
            setActive(true)
        }
    }

    const {
        allSelected,
        selectedIds,
        selectedIdsRef,
        ToggleSingle,
        SetSelection,
        ToggleAll,
        ResetSelection
    } = useComboBox(comboData, 'value', 'multiSelect', props.valueType);

    const {
        optionsCycled,
        filterSelectValue,
        filterSelectItems,
        FilterSelectList,
        ResetFilterSelectOptions,
        HandleFilterSelectBlur,
        HandleFilterSelectFocus
    } = useFilterSelectOptions({ filterData: comboData, onStartCallback: FilterStartCallback });

    useOutsiderDetector(multiSelectRef, () => active ? CloseOptions() : null);

    const ForwardChanges = (data: any) => {
        props.onChange(data);
    }

    const CloseOptions = () => {
        UpdateDisplayValue();
        setActive(props.listAlwaysVisible ?? false);
    }

    const HandleSingleSelect = (item: SelectItemType) => {
        ToggleSingle(item.value, true, true);
    }

    const HandleSelection = (e: any, item: any) => {
        if(e.code && e.code === 'ArrowDown' || e.code === 'ArrowUp') {
            //FilterSelectKeyBoardNav(e);
        } else if(!e.code) {
            ToggleSingle(item.value, e.target.checked);
        }
    }

    const UpdateDisplayValue = () => {
        let formatDisplayValue = selectedIdsRef.current.length > 0 ? selectedIdsRef.current.length === 1 ? `${selectedIdsRef.current.length} Item Selected` : `${selectedIdsRef.current.length} Items Selected` : '';

        if(selectedIdsRef.current.length === 1) {
            const value = GetObjectIndexByKeyValue(comboData, 'value', selectedIdsRef.current[0]);

            if(value !== -1) {
                formatDisplayValue = comboData[value].label;
            }
        }

        setDisplayValue(selectedIdsRef.current.length > 0 ? formatDisplayValue : '');

        UpdateFilterValue();
    }

    const UpdateFilterValue = (fromReset: boolean = false) => {
        if(filterRef.current) {
            filterRef.current.value = active ? filterSelectValue : displayValue;
        }
    }

    const HandleFilterSelectBlurCallback = (e: any) => {
        if(active) {
            setActive(props.listAlwaysVisible ?? false);
        }

        if(props.onBlur) {
            props.onBlur(e);
        }
    }

    const HandleFilterSelectFocusCallback = () => {
        setActive(true);
    }

    useEffect(() => {
        UpdateFilterValue();
    }, [active]);

    useEffect(() => {
        if(optionsCycled) {
            CloseOptions();
        }
    }, [optionsCycled]);

    useEffect(() => {
        if(comboData && props.defaultValues) {
            SetSelection(props.defaultValues);
        }
    }, [comboData, props.defaultValues]);

    useEffect(() => {
        if(comboData) {
            UpdateDisplayValue();
            ForwardChanges(selectedIds);
        }
    }, [comboData, selectedIds, props.isTouched]);

    useEffect(() => {
        if(!comboData || props.data?.length !== comboData.length) {
            setComboData(props.data);
        }
    }, [props.data]);

    useEffect(() => {
        if(props.isReset) {
            SetSelection([]);
        }
    }, [props.isReset]);

    return (
        <div className={'flex-column multi-select-container flex-gap-lg'} ref={multiSelectRef}>
            <div className={props.listAlwaysVisible && !props.minimal ? 'multi-select-inline-control' : ''}>

                <ComboBoxFilter
                    filterRef={filterRef}
                    filterSelectValue={filterSelectValue}
                    placeholder={props.placeholder}
                    label={props.label ?? 'ComboBox'}
                    resetCallback={(e: any) => ResetFilterSelectOptions()}
                    blurCallback={(e: any) => HandleFilterSelectBlur(e, HandleFilterSelectBlurCallback)}
                    filterActionCallback={(e: any) => FilterSelectList(e)}
                    filterNavigationCallback={(e: any) => null} //HandleFilterSelectNavigation(e)}
                    filterFocusCallback={(e: any) => HandleFilterSelectFocus(e, HandleFilterSelectFocusCallback)}
                    minimal={props.minimal}
                    errorMessage={props.error?.message}
                    isValid={comboData && comboData.length && !props.error ? true : null}
                    isActive={active}
                    isLoading={props.isLoading}
                />

                <div className={`${props.listAlwaysVisible && !props.minimal ? 'multi-select-inline-list' : 'generic-menu-container'} ${props.minimal ? 'in-dropdown-menu' : ''} multi-select-entries ${active ? 'multi-select-active' : ''}`} style={{ maxHeight: '300px', overflow: 'auto'}} tabIndex={-1}>
                    {filterSelectValue === '' && (
                        <ComboBoxListActions allSelected={allSelected} toggleCallBack={(e: boolean) => ToggleAll(e)}/>
                    )}

                    {filterSelectItems && filterSelectItems.length === 0 && !props.allowCreation && (
                        <ComboBoxNoResults />
                    )}

                    {filterSelectItems && filterSelectItems.length > 0 &&  filterSelectItems.map((item, index: number) => {
                        if(item.value === undefined || item.value === '') return null;

                        return (
                            <ComboBoxItemToggle
                                key={`comboItem${props.label}${item.value}${index}`}
                                name={props.name}
                                label={props.label}
                                item={item}
                                selectedValues={selectedIds}
                                indeterminateValues={props.indeterminateValues}
                                onChangeCallback={(e: any) => HandleSelection(e, item)}
                                onKeyDownCallback={(e: any) => null} //FilterSelectKeyBoardNav(e)}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
};

export default ComboBox